import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Minus, Pause, Play, Plus, Redo2, Scissors, SkipBack, Trash2, Undo2 } from 'lucide-react';
import Ruler from './Ruler';
import Track from './Track';
import { useZoomPan } from './useZoomPan';
import { useClipDrag } from './useClipDrag';
import { useTimelineKeys } from './useTimelineKeys';
import { probeMediaDuration } from '../decode/frameSource';
import {
    DEFAULT_PIP,
    createItemId,
    formatSeconds,
    getLayerKind,
    mainSequenceEnd,
    sanitizeStreamName,
    sortedClips,
    toSafeNumber,
    trackDisplayRank,
} from '../state/timelineModel';

/**
 * The timeline: ruler, tracks, playhead, and the gestures over them.
 *
 * All state lives in the store passed in — this component owns only what is
 * about *looking* at the timeline (zoom, scroll) rather than what is in it.
 */

const FRAME_STEP = 1 / 30;

/** Whatever length the asset record claims. The field name is inconsistent
 *  across records, and is often missing altogether. */
const declaredDuration = (asset) =>
    toSafeNumber(asset?.Duration ?? asset?.duration ?? asset?.T ?? asset?.t);

/** A dropped asset becomes a clip. Images have no intrinsic length, so they
 *  get a default hold rather than an unusable zero-length clip; `probed` is the
 *  duration read from the file itself, used when the record does not say. */
const clipFromAsset = (asset, kind, probed) => {
    const duration = probed > 0 ? probed : declaredDuration(asset);
    const resolved = duration && duration > 0 ? duration : (kind === 'audio' ? 8 : 4);
    const base = String(asset?.FileAlias || asset?.FileName || kind).replace(/\.[^.]+$/, '');
    const stream = sanitizeStreamName(`${base}_${kind}_${Date.now()}`);

    return {
        id: createItemId(kind),
        label: `${asset.FileName} (${stream})`,
        stream,
        asset_filename: asset.FileName,
        start: 0,
        sourceIn: 0,
        sourceOut: resolved,
        duration: resolved,
        end: resolved,
    };
};

function Timeline({ store, assets, editable, resolveAssetUrl }) {
    const viewportRef = useRef(null);
    const {
        graph, playhead, totalDuration, selection, selectedItem, canUndo, canRedo,
        isPlaying, assetsByFileName, actions,
    } = store;

    const {
        pixelsPerSecond, contentWidth, handleWidth, secondsToPx, pxToSeconds,
        zoomIn, zoomOut, fitToWindow, revealSecond, followPlayhead,
    } = useZoomPan({ totalDuration, viewportRef });

    /* The view travels with playback. Only while playing: when paused the
       playhead moves because someone is scrubbing or dragging, and scrolling
       out from under their pointer would be the timeline fighting them. */
    useEffect(() => {
        if (isPlaying) followPlayhead(playhead);
    }, [isPlaying, playhead, followPlayhead]);

    /* Moves the user asked for but whose destination may be off screen --
       frame steps and back-to-start -- are brought into view as well. */
    const moveToAndReveal = useCallback((seconds) => {
        const target = Math.max(0, seconds);
        actions.setPlayhead(target);
        revealSecond(target);
    }, [actions, revealSecond]);

    /* Everything worth landing exactly on: the start of the edit, the playhead,
       and every other clip's edges. The clip being dragged is excluded so it
       cannot snap to where it already is. */
    const getSnapTargets = useCallback((exceptClipId) => {
        const targets = [0, playhead];
        (graph.layers || []).forEach((layer) => {
            if (getLayerKind(layer) === 'effects') return;
            (layer.items || []).forEach((clip) => {
                if (clip.id === exceptClipId) return;
                targets.push(clip.start, clip.end);
            });
        });
        return targets;
    }, [graph, playhead]);

    const { beginDrag, dragging } = useClipDrag({
        pixelsPerSecond,
        getSnapTargets,
        onMove: actions.moveClip,
        onTrim: actions.trimClip,
    });

    const mainEnd = useMemo(() => mainSequenceEnd(graph), [graph]);

    const beginMove = useCallback((event, clip) => beginDrag(event, clip, 'move'), [beginDrag]);
    const beginTrim = useCallback((event, clip, mode) => beginDrag(event, clip, mode), [beginDrag]);

    const scrub = useCallback((seconds) => {
        actions.setPlaying(false);
        actions.setPlayhead(Math.max(0, seconds));
    }, [actions]);

    /* A dropped clip should land at its true length. When the asset record does
       not carry one, the file is asked before the clip is inserted rather than
       after: inserting a 4-second stub and then correcting it would put a wrong
       edit in the undo history and shuffle everything downstream twice. */
    const handleDropAsset = useCallback(async (layerIndex, kind, fileName, atIndex, atSeconds) => {
        const asset = assetsByFileName.get(fileName);
        if (!asset) return;

        let probed = null;
        if (!(declaredDuration(asset) > 0) && resolveAssetUrl) {
            try {
                probed = await probeMediaDuration(await resolveAssetUrl(fileName));
            } catch {
                probed = null;       // fall back to the default hold
            }
        }
        const clip = clipFromAsset(asset, kind, probed);
        if (kind === 'overlay') {
            /* An inset starts with the main video's sound: it is usually a
               reaction or a screen recording laid over someone already
               talking, and two voices at once is rarely what was meant. The
               inspector switches it to the inset's sound, or both. */
            actions.placeClip(layerIndex, {
                ...clip, params: { ...DEFAULT_PIP }, soundFrom: 'main',
            }, atSeconds);
            return;
        }
        actions.insertClip(layerIndex, clip, atIndex);
    }, [assetsByFileName, actions, resolveAssetUrl]);

    // Top to bottom as an editor stacks them -- insets above the video they
    // cover -- while each keeps its real index in the graph.
    const displayLayers = useMemo(() => (graph.layers || [])
        .map((layer, layerIndex) => ({ layer, layerIndex }))
        .sort((a, b) => trackDisplayRank(a.layer) - trackDisplayRank(b.layer)),
    [graph.layers]);

    /* Split acts on the selected clip when the playhead is inside it, and
       otherwise on whatever clip the playhead is over — pressing S with
       nothing selected should still cut. */
    const splitTarget = useMemo(() => {
        const isInside = (clip) => clip && playhead > clip.start && playhead < clip.end;
        if (isInside(selectedItem)) return selectedItem;
        const videoLayer = graph.layers?.[store.videoLayerIndex];
        return sortedClips(videoLayer).find(isInside) || null;
    }, [selectedItem, playhead, graph, store.videoLayerIndex]);

    useTimelineKeys({
        enabled: editable,
        onPlayPause: () => actions.setPlaying(!isPlaying),
        onStep: (direction) => {
            actions.setPlaying(false);
            moveToAndReveal(playhead + direction * FRAME_STEP);
        },
        onSplit: () => splitTarget && actions.splitAt(splitTarget.id, playhead),
        onDelete: () => selection && actions.deleteItem(selection.id),
        onUndo: actions.undo,
        onRedo: actions.redo,
        onTrimToPlayhead: (edge) => {
            if (!selectedItem) return;
            const anchor = edge === 'left' ? selectedItem.start : selectedItem.end;
            actions.trimClip(selectedItem.id, edge, playhead - anchor);
        },
    });

    return (
        <div className="nle-timeline-panel">
            <div className="nle-toolbar">
                {/* Transport lives here as well as on the stage. Watching a cut
                    back is part of trimming it, and the timeline is where the
                    trimming happens -- on a tall edit the stage's own buttons
                    are off the top of the screen, which made every play a
                    scroll up and every pause a scroll back down. */}
                <div className="nle-toolbar-group">
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => actions.setPlaying(!isPlaying)}
                        title={isPlaying ? 'Pause (space)' : 'Play (space)'}
                        aria-label={isPlaying ? 'Pause' : 'Play'}
                    >{isPlaying ? <Pause size={16} aria-hidden="true" /> : <Play size={16} aria-hidden="true" />}</button>
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                            actions.setPlaying(false);
                            moveToAndReveal(0);
                        }}
                        title="Back to start"
                        aria-label="Back to start"
                    ><SkipBack size={16} aria-hidden="true" /></button>
                </div>

                <div className="nle-toolbar-group">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={actions.undo} disabled={!canUndo} title="Undo (Ctrl+Z)" aria-label="Undo"><Undo2 size={16} aria-hidden="true" /></button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={actions.redo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)" aria-label="Redo"><Redo2 size={16} aria-hidden="true" /></button>
                </div>

                <div className="nle-toolbar-group">
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => splitTarget && actions.splitAt(splitTarget.id, playhead)}
                        disabled={!editable || !splitTarget}
                        title="Split at playhead (S)"
                    ><Scissors size={14} aria-hidden="true" /> Split</button>
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => selection && actions.deleteItem(selection.id)}
                        disabled={!editable || !selection}
                        title="Delete selected (Del)"
                    ><Trash2 size={14} aria-hidden="true" /> Delete</button>
                </div>

                <span className="nle-time-readout">
                    {formatSeconds(playhead)} / {formatSeconds(totalDuration)}
                </span>

                <div className="nle-toolbar-group nle-toolbar-right">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={zoomOut} title="Zoom out" aria-label="Zoom out"><Minus size={16} aria-hidden="true" /></button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={fitToWindow} title="Fit to window">Fit</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={zoomIn} title="Zoom in" aria-label="Zoom in"><Plus size={16} aria-hidden="true" /></button>
                </div>
            </div>

            {/* Every lane is a fixed-width label followed by the track itself,
                so the ruler needs a matching spacer and the playhead a matching
                offset — otherwise time zero sits at a different x in each row. */}
            <div className={`nle-viewport ${dragging ? `dragging-${dragging.mode}` : ''}`} ref={viewportRef}>
                <div
                    className="nle-scroll-content"
                    style={{ width: `calc(var(--nle-label-width) + ${contentWidth}px)` }}
                >
                    <div className="nle-ruler-row">
                        <div className="nle-track-label nle-ruler-spacer" />
                        <Ruler
                            pixelsPerSecond={pixelsPerSecond}
                            contentWidth={contentWidth}
                            playhead={playhead}
                            totalDuration={totalDuration}
                            onScrub={scrub}
                        />
                    </div>

                    {displayLayers.map(({ layer, layerIndex }) => {
                        const kind = getLayerKind(layer) || 'video';
                        return (
                            <Track
                                key={layer.name || layerIndex}
                                layer={layer}
                                kind={kind}
                                editable={editable}
                                contentWidth={contentWidth}
                                secondsToPx={secondsToPx}
                                handleWidth={handleWidth}
                                pxToSeconds={pxToSeconds}
                                selectionId={selection?.id}
                                onSelect={(id) => actions.select(id ? { kind: kind === 'effects' ? 'effect' : 'clip', id } : null)}
                                onBeginMove={beginMove}
                                onBeginTrim={beginTrim}
                                onDropAsset={(fileName, atIndex, atSeconds) => handleDropAsset(layerIndex, kind, fileName, atIndex, atSeconds)}
                                onScrubToBackground={scrub}
                                mainEnd={kind === 'overlay' ? mainEnd : null}
                            />
                        );
                    })}

                    {/* Drawn over every track, so one line reads across all of
                        them — a per-track playhead makes it impossible to see
                        what is under the cursor on the track below. */}
                    <div
                        className="nle-playhead"
                        style={{ left: `calc(var(--nle-label-width) + ${secondsToPx(playhead)}px)` }}
                    >
                        <div className="nle-playhead-grip" />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Timeline;
